const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export { API_BASE };

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail ?? message;
  }
}

function getToken(): string | null {
  return localStorage.getItem("token");
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem("token", token);
  else localStorage.removeItem("token");
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  auth = true,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type") && options.body && !(options.body instanceof URLSearchParams)) {
    headers.set("Content-Type", "application/json");
  }
  if (auth) {
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }

  if (!res.ok) {
    const rawDetail =
      typeof data === "object" && data && "detail" in data
        ? (data as { detail: unknown }).detail
        : res.statusText;
    throw new ApiError(res.status, formatApiDetail(rawDetail, res.statusText), rawDetail);
  }
  return data as T;
}

/** Turn FastAPI/Pydantic `detail` (string or validation error list) into a user-facing message. */
function formatApiDetail(rawDetail: unknown, fallback: string): string {
  if (typeof rawDetail === "string" && rawDetail.trim()) return rawDetail;
  if (
    typeof rawDetail === "object" &&
    rawDetail &&
    "message" in rawDetail &&
    typeof (rawDetail as { message: unknown }).message === "string"
  ) {
    return (rawDetail as { message: string }).message;
  }
  if (Array.isArray(rawDetail)) {
    const parts = rawDetail
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const err = item as { loc?: unknown; msg?: unknown };
        const msg = typeof err.msg === "string" ? err.msg : null;
        if (!msg) return null;
        const loc = Array.isArray(err.loc)
          ? err.loc.filter((p): p is string => typeof p === "string" && p !== "body").join(".")
          : "";
        if (loc === "password" && /at least|min/i.test(msg)) {
          return "Password must be at least 8 characters";
        }
        if (loc === "email") {
          return "Please enter a valid email address";
        }
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter((p): p is string => Boolean(p));
    if (parts.length) return parts.join(". ");
  }
  return fallback;
}

export type AuthToken = {
  access_token: string;
  token_type?: string;
  is_new_user?: boolean;
};

export type AuthProviders = {
  google_client_id: string | null;
  google_enabled?: boolean;
  apple_client_id: string | null;
  apple_redirect_uri: string | null;
};

export const api = {
  register: (email: string, password: string, child_name: string) =>
    request<AuthToken>(
      "/auth/register",
      { method: "POST", body: JSON.stringify({ email, password, child_name }) },
      false,
    ),
  login: (email: string, password: string) => {
    const body = new URLSearchParams();
    body.set("username", email);
    body.set("password", password);
    return request<AuthToken>(
      "/auth/login",
      { method: "POST", body, headers: { "Content-Type": "application/x-www-form-urlencoded" } },
      false,
    );
  },
  authProviders: () => request<AuthProviders>("/auth/providers", {}, false),
  googleLogin: (id_token: string) =>
    request<AuthToken>("/auth/google", { method: "POST", body: JSON.stringify({ id_token }) }, false),
  appleLogin: (id_token: string, nonce?: string) =>
    request<AuthToken>(
      "/auth/apple",
      { method: "POST", body: JSON.stringify({ id_token, nonce }) },
      false,
    ),
  me: () => request<import("./types").User>("/auth/me"),
  listChildren: () => request<import("./types").Child[]>("/children"),
  createChild: (data: { name: string }) =>
    request<import("./types").Child>("/children", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateChild: (childId: number, data: { name: string }) =>
    request<import("./types").Child>(`/children/${childId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteChild: (childId: number) =>
    request<void>(`/children/${childId}`, { method: "DELETE" }),
  listYears: () => request<import("./types").AcademicYear[]>(`/years`),
  getYearCalendar: (yearId: number, childId?: number) => {
    const q = childId != null ? `?child_id=${childId}` : "";
    return request<import("./types").YearCalendar>(`/years/${yearId}/calendar${q}`);
  },
  createTerm: (
    yearId: number,
    data: {
      name?: string;
      order_index?: number;
      start_date?: string | null;
      end_date?: string | null;
      teaching_weeks?: number;
    } = {},
  ) =>
    request<import("./types").Term>(`/years/${yearId}/terms`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateTerm: (
    termId: number,
    data: Partial<import("./types").Term> & { confirm_trim_lessons?: boolean },
  ) =>
    request<import("./types").Term>(`/terms/${termId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteTerm: (termId: number) =>
    request<void>(`/terms/${termId}`, { method: "DELETE" }),
  getTermSchemeImpact: (termId: number) =>
    request<{ lesson_count: number; capacity: number; excess_count: number }>(
      `/terms/${termId}/scheme-impact`,
    ),
  createHoliday: (
    yearId: number,
    data: {
      name: string;
      order_index?: number;
      start_date?: string | null;
      end_date?: string | null;
    },
  ) =>
    request<import("./types").Holiday>(`/years/${yearId}/holidays`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateHoliday: (holidayId: number, data: Partial<import("./types").Holiday>) =>
    request<import("./types").Holiday>(`/holidays/${holidayId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteHoliday: (holidayId: number) =>
    request<void>(`/holidays/${holidayId}`, { method: "DELETE" }),
  createNationalHoliday: (
    yearId: number,
    data: { name: string; start_date: string; end_date?: string },
  ) =>
    request<import("./types").NationalHoliday>(`/years/${yearId}/national-holidays`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateNationalHoliday: (
    holidayId: number,
    data: Partial<import("./types").NationalHoliday>,
  ) =>
    request<import("./types").NationalHoliday>(`/national-holidays/${holidayId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteNationalHoliday: (holidayId: number) =>
    request<void>(`/national-holidays/${holidayId}`, { method: "DELETE" }),
  listAbsences: (childId: number) =>
    request<import("./types").Absence[]>(`/children/${childId}/absences`),
  createAbsence: (
    childId: number,
    data: { start_date: string; end_date?: string; reason?: string | null },
  ) =>
    request<import("./types").Absence>(`/children/${childId}/absences`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateAbsence: (
    absenceId: number,
    data: Partial<Pick<import("./types").Absence, "start_date" | "end_date" | "reason">>,
  ) =>
    request<import("./types").Absence>(`/absences/${absenceId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteAbsence: (absenceId: number) =>
    request<void>(`/absences/${absenceId}`, { method: "DELETE" }),
  clearHoliday: (holidayId: number) =>
    request<import("./types").Holiday>(`/holidays/${holidayId}`, {
      method: "PATCH",
      body: JSON.stringify({ start_date: null, end_date: null }),
    }),
  createYear: (data: {
    name: string;
    start_month: number;
    start_date?: string;
    end_date?: string;
  }) =>
    request<import("./types").AcademicYear>(`/years`, {
      method: "POST",
      body: JSON.stringify({ ...data, create_default_terms: false, is_active: false }),
    }),
  updateYear: (yearId: number, data: Partial<import("./types").AcademicYear>) =>
    request<import("./types").AcademicYear>(`/years/${yearId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteYear: (yearId: number) =>
    request<void>(`/years/${yearId}`, { method: "DELETE" }),
  listTopics: (childId: number) =>
    request<import("./types").Topic[]>(`/children/${childId}/topics`),
  createTopic: (
    childId: number,
    data: { name: string; color: string },
  ) =>
    request<import("./types").Topic>(`/children/${childId}/topics`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateTopic: (
    topicId: number,
    data: {
      name?: string;
      color?: string;
    },
  ) =>
    request<import("./types").Topic>(`/topics/${topicId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteTopic: (topicId: number) =>
    request<void>(`/topics/${topicId}`, { method: "DELETE" }),
  getTimetable: (childId: number) =>
    request<import("./types").Timetable>(`/children/${childId}/timetable`),
  getTimetableWeek: (childId: number, termId: number, weekIndex: number) =>
    request<import("./types").TimetableWeek>(
      `/children/${childId}/timetable/week?term_id=${termId}&week_index=${weekIndex}`,
    ),
  getSuggestedTimetableWeek: (childId: number, asOf?: string) => {
    const q = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
    return request<import("./types").TimetableSuggestedWeek>(
      `/children/${childId}/timetable/suggested-week${q}`,
    );
  },
  updateTimetable: (
    childId: number,
    data: {
      day_start: string;
      day_end: string;
      breaks: {
        client_key?: string;
        term_id?: number | null;
        name: string;
        weekday: number;
        start_time: string;
        duration_minutes: number;
        week_interval?: number;
        week_phase?: number;
      }[];
      recurring_events?: {
        client_key?: string;
        term_id?: number | null;
        name: string;
        weekday: number;
        start_time: string;
        duration_minutes: number;
        week_interval?: number;
        week_phase?: number;
      }[];
      slots: {
        client_key?: string;
        term_id?: number | null;
        topic_id: number;
        weekday: number;
        start_time: string;
        duration_minutes: number;
        period_units: number;
        week_interval?: number;
        week_phase?: number;
      }[];
      exceptions?: import("./types").TimetableException[];
    },
  ) =>
    request<import("./types").Timetable>(`/children/${childId}/timetable`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  getScheme: (topicId: number, termId: number) =>
    request<import("./types").SchemeBundle>(`/topics/${topicId}/terms/${termId}/scheme`),
  getSchemeLessons: (topicId: number, termId: number, numbers: number[]) => {
    const unique = [...new Set(numbers.filter((n) => n >= 1))];
    const qs = encodeURIComponent(unique.join(","));
    return request<import("./types").SchemeLesson[]>(
      `/topics/${topicId}/terms/${termId}/scheme/lessons?numbers=${qs}`,
    );
  },
  getLessonPlanning: (childId: number, termId: number, from: string, days = 7) =>
    request<import("./types").LessonPlanningFeed>(
      `/children/${childId}/terms/${termId}/lesson-planning?from=${encodeURIComponent(from)}&days=${days}`,
    ),
  putScheme: (
    topicId: number,
    termId: number,
    lessons: {
      lesson_number: number;
      title: string;
      lesson_goals?: string;
      description: string;
      completed?: boolean;
      prep_tasks?: { id: string; label: string; done: boolean }[];
      lesson_resources?: import("./types").LessonResource[];
    }[],
  ) =>
    request<import("./types").SchemeBundle>(`/topics/${topicId}/terms/${termId}/scheme`, {
      method: "PUT",
      body: JSON.stringify({ lessons }),
    }),
  putSchemeLesson: (
    topicId: number,
    termId: number,
    lesson: {
      lesson_number: number;
      title: string;
      lesson_goals?: string;
      description: string;
      completed?: boolean;
      prep_tasks?: { id: string; label: string; done: boolean }[];
      lesson_resources?: import("./types").LessonResource[];
    },
  ) =>
    request<import("./types").SchemeLesson>(
      `/topics/${topicId}/terms/${termId}/scheme/lessons/${lesson.lesson_number}`,
      {
        method: "PUT",
        body: JSON.stringify({
          lesson_number: lesson.lesson_number,
          title: lesson.title,
          lesson_goals: lesson.lesson_goals ?? "",
          description: lesson.description,
          completed: !!lesson.completed,
          prep_tasks: lesson.prep_tasks ?? [],
          lesson_resources: lesson.lesson_resources ?? [],
        }),
      },
    ),
  previewReschedule: (
    childId: number,
    termId: number,
    data: {
      mode: "lesson" | "day";
      date: string;
      items: { topic_id: number; lesson_number: number }[];
    },
  ) =>
    request<import("./types").ReschedulePreview>(
      `/children/${childId}/terms/${termId}/reschedule/preview`,
      { method: "POST", body: JSON.stringify(data) },
    ),
  executeReschedule: (
    childId: number,
    termId: number,
    data: {
      mode: "lesson" | "day";
      date: string;
      items: { topic_id: number; lesson_number: number }[];
    },
  ) =>
    request<import("./types").ReschedulePreview>(
      `/children/${childId}/terms/${termId}/reschedule`,
      { method: "POST", body: JSON.stringify(data) },
    ),
  uploadLessonResource: async (
    topicId: number,
    termId: number,
    lessonNumber: number,
    file: File,
  ) => {
    const headers = new Headers();
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const body = new FormData();
    body.append("file", file);
    const res = await fetch(
      `${API_BASE}/topics/${topicId}/terms/${termId}/scheme/lessons/${lessonNumber}/resources/upload`,
      { method: "POST", headers, body },
    );
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { detail: text };
      }
    }
    if (!res.ok) {
      const detail =
        typeof data === "object" && data && "detail" in data
          ? String((data as { detail: unknown }).detail)
          : res.statusText;
      throw new ApiError(res.status, detail);
    }
    return data as import("./types").LessonResource;
  },
  downloadLessonResource: async (
    topicId: number,
    termId: number,
    lessonNumber: number,
    resourceId: string,
    filename: string,
  ) => {
    const headers = new Headers();
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/topics/${topicId}/terms/${termId}/scheme/lessons/${lessonNumber}/resources/${resourceId}/file`,
      { headers },
    );
    if (!res.ok) {
      const text = await res.text();
      let detail = res.statusText;
      try {
        const data = JSON.parse(text) as { detail?: unknown };
        if (data.detail != null) detail = String(data.detail);
      } catch {
        if (text) detail = text;
      }
      throw new ApiError(res.status, detail);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
